import { css, html, TemplateResult } from "lit";
import { customElement } from "lit/decorators.js";
import { t } from "../../translation/i18n";
import { BaseElement } from "./BaseElement";
import("./RotatePane");

/**
 * Shared canvas float menu for configurator and room designer.
 * Products enable actions via attributes; handlers listen for events.
 *
 * Light-DOM styles are forwarded into a parent shadow root — selectors must
 * target the custom element tags (not `:host`).
 */
@customElement("float-menu")
export class FloatMenu extends BaseElement {
  static styles = css`
    float-menu,
    model-float-menu {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      background-color: #fff;
      box-shadow: 0 2px 8px #0000001f;
      border-radius: 2rem;
      display: flex;
      flex-direction: column;
      align-items: center;
      z-index: 10;
    }

    float-menu .float-menu-stack,
    model-float-menu .float-menu-stack {
      display: flex;
      flex-direction: column;
      align-items: center;
    }

    float-menu .float-menu-row,
    model-float-menu .float-menu-row {
      display: flex;
      flex-direction: row;
      align-items: center;
    }

    float-menu .float-menu-row--sub,
    model-float-menu .float-menu-row--sub {
      padding-top: 2px;
    }

    float-menu .svg,
    model-float-menu .svg {
      width: 20.5px;
      height: 20.5px;
      background-color: #f7f7f7e6;
    }

    float-menu .button,
    float-menu .float-menu-button,
    model-float-menu .button,
    model-float-menu .float-menu-button {
      height: 40px;
      width: 40px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      background-color: #f7f7f7e6;
      cursor: pointer;
      border: none;
      color: inherit;
      fill: currentColor;
    }

    /* Fabric swatches — larger target; multi-color SVG (not currentColor). */
    float-menu .button-material,
    model-float-menu .button-material {
      width: 46px;
      height: 46px;
      fill: unset;
    }

    float-menu .button-material .svg,
    model-float-menu .button-material .svg {
      width: 28px;
      height: 28px;
      background-color: transparent;
      overflow: visible;
      fill: unset;
    }

    float-menu .button:active,
    float-menu .float-menu-button:active,
    float-menu .button.selected,
    float-menu .float-menu-button.selected,
    model-float-menu .button:active,
    model-float-menu .float-menu-button:active,
    model-float-menu .button.selected,
    model-float-menu .float-menu-button.selected {
      background-color: #111;
      color: #fff;
      fill: #fff;
    }

    float-menu .button-material:active,
    model-float-menu .button-material:active {
      fill: unset;
      opacity: 0.85;
    }

    float-menu .button:hover,
    float-menu .float-menu-button:hover,
    model-float-menu .button:hover,
    model-float-menu .float-menu-button:hover {
      background-color: #ffffff;
    }

    float-menu .float-menu-axis,
    model-float-menu .float-menu-axis {
      display: inline-flex;
      height: 32px;
      margin: 0 2px;
      border: 1px solid #e5e5e5;
      border-radius: 999px;
      overflow: hidden;
      background: #fff;
    }

    float-menu .float-menu-axis button,
    model-float-menu .float-menu-axis button {
      appearance: none;
      border: none;
      margin: 0;
      padding: 0;
      width: 32px;
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #666;
      background: transparent;
      cursor: pointer;
    }

    float-menu .float-menu-axis button svg,
    model-float-menu .float-menu-axis button svg {
      width: 16px;
      height: 16px;
    }

    float-menu .float-menu-axis button + button,
    model-float-menu .float-menu-axis button + button {
      border-left: 1px solid #e5e5e5;
    }

    float-menu .float-menu-axis button.active,
    model-float-menu .float-menu-axis button.active {
      background: #111;
      color: #fff;
    }

    float-menu .float-menu-angle,
    model-float-menu .float-menu-angle {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      height: 32px;
      margin: 0 2px;
      padding: 0 8px 0 6px;
      border-radius: 999px;
      background: #fff;
      border: 1px solid #e5e5e5;
      color: #666;
    }

    float-menu .float-menu-angle svg,
    model-float-menu .float-menu-angle svg {
      width: 16px;
      height: 16px;
      flex: 0 0 auto;
    }

    float-menu .float-menu-angle input,
    model-float-menu .float-menu-angle input {
      width: 40px;
      height: 24px;
      box-sizing: border-box;
      border: 1px solid #d4d4d4;
      border-radius: 6px;
      text-align: center;
      font: 600 13px inherit;
      color: #222;
      background: #fff;
    }

    float-menu .float-menu-angle span,
    model-float-menu .float-menu-angle span {
      font-size: 12px;
      font-weight: 600;
    }
  `;

  private _rotateOn = "false";
  private _rotation? = "false";
  private _replace? = "true";
  private _reload? = "false";
  private _delete? = "true";
  private _material? = "false";
  private _measure? = "false";
  private _measureAuto? = "false";
  private _resize? = "false";
  private _copy? = "false";
  private _axisMove? = "false";
  private _axisMode = "horizontal";
  private _angle? = "false";
  private _angleValue = "90";
  private _angleMin = "0";
  private _angleMax = "180";
  private _gizmo? = "false";
  private _gizmoOn? = "false";
  private _active = "";

  /** Latched action name from `active` (`replace`, `measure`, …). */
  private _isActive(action: string): boolean {
    return this._active === action;
  }

  private _actionClass(action: string, extra = ""): string {
    return `button float-menu-button${extra}${
      this._isActive(action) ? " selected" : ""
    }`;
  }

  private _press(action: string): void {
    this._active = action;
    this.setAttribute("active", action);
    this.requestUpdate();
  }

  /** Returns whether the action is now latched on. */
  private _toggle(action: string): boolean {
    if (this._active === action) {
      this._active = "";
      this.removeAttribute("active");
      this.requestUpdate();
      return false;
    }
    this._press(action);
    return true;
  }

  private _handleGizmo(): void {
    const enabled = this._gizmoOn !== "true";
    this._gizmoOn = enabled ? "true" : "false";
    this.setAttribute("gizmo-on", this._gizmoOn);
    this.requestUpdate();
    this._emit("float-gizmo", { enabled });
  }

  private _handleRotateOn() {
    this._press("rotate");
    this._rotateOn = "true";
    this.requestUpdate();
    this._emit("float-rotate");
  }

  private _handleRotateEnd(e: any) {
    this.dispatchEvent(
      new CustomEvent("rotate-end", {
        ...e,
        bubbles: true,
        composed: true,
        detail: e.detail,
      }),
    );
  }

  private _handleRotateChange(e: any) {
    this.dispatchEvent(
      new CustomEvent("rotate-change", {
        ...e,
        bubbles: true,
        composed: true,
        detail: e.detail,
      }),
    );
  }

  private _emit(name: string, detail?: unknown) {
    this.dispatchEvent(
      new CustomEvent(name, { bubbles: true, composed: true, detail }),
    );
  }

  private _handleDeleteModel() {
    this._emit("delete");
    this._emit("float-delete");
  }

  private _handleReplaceModel() {
    const active = this._toggle("replace");
    this._emit("replace", { active });
    this._emit("float-replace", { active });
  }

  private _handleReloadModel() {
    this._press("reload");
    this._emit("float-reload");
  }

  connectedCallback(): void {
    super.connectedCallback();
    this._rotation = this.getAttribute("rotation") || undefined;
    this._delete = this.getAttribute("delete") || undefined;
    this._replace = this.getAttribute("replace") || undefined;
    this._reload = this.getAttribute("reload") || undefined;
    this._material = this.getAttribute("material") || undefined;
    this._measure = this.getAttribute("measure") || undefined;
    this._measureAuto = this.getAttribute("measure-auto") || undefined;
    this._resize = this.getAttribute("resize") || undefined;
    this._copy = this.getAttribute("copy") || undefined;
    this._axisMove = this.getAttribute("axis-move") || undefined;
    this._axisMode = this.getAttribute("axis-mode") || "horizontal";
    this._angle = this.getAttribute("angle") || undefined;
    this._angleValue = this.getAttribute("angle-value") || "90";
    this._angleMin = this.getAttribute("angle-min") || "0";
    this._angleMax = this.getAttribute("angle-max") || "180";
    this._gizmo = this.getAttribute("gizmo") || undefined;
    this._gizmoOn = this.getAttribute("gizmo-on") || undefined;
    this._active = this.getAttribute("active") || "";
    this.requestUpdate();
  }

  attributeChangedCallback(
    name: string,
    _old: string | null,
    value: string | null,
  ): void {
    if (name === "rotation") this._rotation = value || undefined;
    if (name === "delete") this._delete = value || undefined;
    if (name === "replace") this._replace = value || undefined;
    if (name === "reload") this._reload = value || undefined;
    if (name === "material") this._material = value || undefined;
    if (name === "measure") this._measure = value || undefined;
    if (name === "measure-auto") this._measureAuto = value || undefined;
    if (name === "resize") this._resize = value || undefined;
    if (name === "copy") this._copy = value || undefined;
    if (name === "axis-move") this._axisMove = value || undefined;
    if (name === "axis-mode") this._axisMode = value || "horizontal";
    if (name === "angle") this._angle = value || undefined;
    if (name === "angle-value") this._angleValue = value || "90";
    if (name === "angle-min") this._angleMin = value || "0";
    if (name === "angle-max") this._angleMax = value || "180";
    if (name === "gizmo") this._gizmo = value || undefined;
    if (name === "gizmo-on") this._gizmoOn = value || undefined;
    if (name === "active") this._active = value || "";
    this.requestUpdate();
  }

  static get observedAttributes() {
    return [
      "rotation",
      "delete",
      "replace",
      "reload",
      "material",
      "measure",
      "measure-auto",
      "resize",
      "copy",
      "axis-move",
      "axis-mode",
      "angle",
      "angle-value",
      "angle-min",
      "angle-max",
      "gizmo",
      "gizmo-on",
      "active",
    ];
  }

  protected renderHtml(): TemplateResult {
    const buttons = html`${this._axisMove === "true"
          ? html`<div
              class="float-menu-axis"
              role="group"
              aria-label=${t("structural.moveMode")}
              @pointerdown=${(event: PointerEvent) => event.stopPropagation()}
            >
              <button
                type="button"
                class=${this._axisMode === "horizontal" ? "active" : ""}
                title=${t("structural.moveHorizontal")}
                aria-label=${t("structural.moveHorizontal")}
                @click=${() => this._setAxisMode("horizontal")}
              >
                ${axisArrowIcon("horizontal")}
              </button>
              <button
                type="button"
                class=${this._axisMode === "vertical" ? "active" : ""}
                title=${t("structural.moveVertical")}
                aria-label=${t("structural.moveVertical")}
                @click=${() => this._setAxisMode("vertical")}
              >
                ${axisArrowIcon("vertical")}
              </button>
            </div>`
          : ``}
        ${this._angle === "true"
          ? html`<label
              class="float-menu-angle"
              title=${t("door.openAngle")}
              aria-label=${t("door.openAngle")}
              @pointerdown=${(event: PointerEvent) => event.stopPropagation()}
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path
                  fill="currentColor"
                  d="M4.5 19.5V4.5h2v13.2l9.3-5.4.05 7.7H4.5Zm12.05-6.05L7.7 7.9V5.7l10.2 5.9-1.35 1.85ZM19 19.5h-1.9l-.1-6.55 2-.85V19.5Z"
                />
              </svg>
              <input
                type="number"
                min=${this._angleMin}
                max=${this._angleMax}
                step="5"
                .value=${this._angleValue}
                @input=${this._previewAngle}
                @change=${this._commitAngle}
                @keydown=${(event: KeyboardEvent) => {
                  if (event.key === "Enter") {
                    (event.currentTarget as HTMLInputElement).blur();
                  }
                }}
              />
              <span>°</span>
            </label>`
          : ``}
        ${this._gizmo === "true"
          ? html`<div
              class="button float-menu-button${this._gizmoOn === "true"
                ? " selected"
                : ""}"
              title=${t("structural.moveGizmo")}
              aria-label=${t("structural.moveGizmo")}
              aria-pressed=${this._gizmoOn === "true" ? "true" : "false"}
              @click=${this._handleGizmo}
            >
              <svg
                class="svg"
                viewBox="0 0 24 24"
                aria-hidden="true"
                fill="none"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
                style="fill: none"
              >
                <path d="M12 2v7M9.15 4.85 12 2l2.85 2.85" />
                <path d="M12 22v-7M9.15 19.15 12 22l2.85-2.85" />
                <path d="M2 12h7M4.85 9.15 2 12l2.85 2.85" />
                <path d="M22 12h-7M19.15 9.15 22 12l-2.85 2.85" />
              </svg>
            </div>`
          : ``}
        ${this._rotation === "true"
          ? html`<div
              class=${this._actionClass("rotate")}
              @click=${this._handleRotateOn}
            >
              <svg class="svg" data-v-a281fab8-s="">
                <use xlink:href="#icon-rotate2"></use>
              </svg>
            </div>`
          : ``}
        ${this._delete === "true"
          ? html`<div
              class=${this._actionClass("delete")}
              @click=${this._handleDeleteModel}
            >
              <svg class="svg container-svg" data-v-a281fab8-s="">
                <use xlink:href="#icon-delete"></use>
              </svg>
            </div> `
          : ``}
        ${this._copy === "true"
          ? html`<div
              class=${this._actionClass("copy")}
              title="Kopyala"
              aria-label="Kopyala"
              @click=${() => {
                this._press("copy");
                this._emit("float-copy");
              }}
            >
              <svg class="svg container-svg" aria-hidden="true">
                <use xlink:href="#icon-duplicate"></use>
              </svg>
            </div>`
          : ``}
        ${this._replace === "true"
          ? html`<div
              class=${this._actionClass("replace")}
              aria-pressed=${this._isActive("replace") ? "true" : "false"}
              @click=${this._handleReplaceModel}
            >
              <svg class="svg container-svg" data-v-a281fab8-s="">
                <use xlink:href="#icon-change_product"></use>
              </svg>
            </div>`
          : ``}
        ${this._reload === "true"
          ? html`<div
              class=${this._actionClass("reload")}
              title="Tekrar yükle"
              aria-label="Tekrar yükle"
              @click=${this._handleReloadModel}
            >
              <svg class="svg container-svg" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M12 5V1L7 6l5 5V7c3.31 0 6 2.69 6 6s-2.69 6-6 6-6-2.69-6-6H4c0 4.42 3.58 8 8 8s8-3.58 8-8-3.58-8-8-8z" />
              </svg>
            </div>`
          : ``}
        ${this._material === "true"
          ? html`<div
              class=${this._actionClass("material", " button-material")}
              title="Kumaş / Kartela"
              aria-label="Kumaş / Kartela"
              @click=${() => {
                this._press("material");
                this._emit("float-open-material");
              }}
            >
              <svg
                class="svg"
                viewBox="0 0 24 24"
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
              >
                <g transform="translate(9.2 13.3) rotate(-26)">
                  <path
                    style="fill:#FF4D52"
                    d="M-4.8-5.6L-4-6.4L-3.2-5.6L-2.4-6.4L-1.6-5.6L-.8-6.4L0-5.6L.8-6.4L1.6-5.6L2.4-6.4L3.2-5.6L4-6.4L4.8-5.6L5.6-4.8L4.8-4L5.6-3.2L4.8-2.4L5.6-1.6L4.8-.8L5.6 0L4.8.8L5.6 1.6L4.8 2.4L5.6 3.2L4.8 4L5.6 4.8L4.8 5.6L4 6.4L3.2 5.6L2.4 6.4L1.6 5.6L.8 6.4L0 5.6L-.8 6.4L-1.6 5.6L-2.4 6.4L-3.2 5.6L-4 6.4L-4.8 5.6L-5.6 4.8L-4.8 4L-5.6 3.2L-4.8 2.4L-5.6 1.6L-4.8.8L-5.6 0L-4.8-.8L-5.6-1.6L-4.8-2.4L-5.6-3.2L-4.8-4L-5.6-4.8Z"
                  />
                </g>
                <g transform="translate(14.5 11.1) rotate(20)">
                  <path
                    style="fill:#5FC9C3"
                    d="M-4.8-5.6L-4-6.4L-3.2-5.6L-2.4-6.4L-1.6-5.6L-.8-6.4L0-5.6L.8-6.4L1.6-5.6L2.4-6.4L3.2-5.6L4-6.4L4.8-5.6L5.6-4.8L4.8-4L5.6-3.2L4.8-2.4L5.6-1.6L4.8-.8L5.6 0L4.8.8L5.6 1.6L4.8 2.4L5.6 3.2L4.8 4L5.6 4.8L4.8 5.6L4 6.4L3.2 5.6L2.4 6.4L1.6 5.6L.8 6.4L0 5.6L-.8 6.4L-1.6 5.6L-2.4 6.4L-3.2 5.6L-4 6.4L-4.8 5.6L-5.6 4.8L-4.8 4L-5.6 3.2L-4.8 2.4L-5.6 1.6L-4.8.8L-5.6 0L-4.8-.8L-5.6-1.6L-4.8-2.4L-5.6-3.2L-4.8-4L-5.6-4.8Z"
                  />
                </g>
              </svg>
            </div>`
          : ``}
        ${this._measure === "true"
          ? html`<div
              class=${this._actionClass("measure")}
              title="Metre"
              aria-pressed=${this._isActive("measure") ? "true" : "false"}
              @click=${this._handleMeasureMenu}
            >
              <svg class="svg container-svg">
                <use xlink:href="#icon-measurements"></use>
              </svg>
            </div>`
          : ``}
        ${this._resize === "true"
          ? html`<div
              class=${this._actionClass("resize")}
              title="Resize"
              @click=${() => {
                this._press("resize");
                this._emit("float-resize");
              }}
            >
              <svg class="svg container-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M15 3h6v6" />
                <path d="M9 21H3v-6" />
                <path d="M21 3l-7 7" />
                <path d="M3 21l7-7" />
              </svg>
            </div>`
          : ``}`;

    return html` ${this._rotateOn === "true"
      ? html`<rotate-pane
          @end=${this._handleRotateEnd}
          @change=${this._handleRotateChange}
        ></rotate-pane>`
      : html`<div class="float-menu-stack">
          <div class="float-menu-row">${buttons}</div>
          ${this._measure === "true" && this._isActive("measure")
            ? html`<div class="float-menu-row float-menu-row--sub">
                <div
                  class="button float-menu-button${this._measureAuto === "true"
                    ? " selected"
                    : ""}"
                  title="Otomatik (4 yön)"
                  aria-label="Otomatik metre"
                  aria-pressed=${this._measureAuto === "true" ? "true" : "false"}
                  @click=${this._handleMeasureAuto}
                >
                  <svg
                    class="svg container-svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="1.8"
                    aria-hidden="true"
                  >
                    <rect x="7" y="7" width="10" height="10" rx="1" />
                    <path d="M12 7V3M12 21v-4M7 12H3M21 12h-4" />
                  </svg>
                </div>
                <div
                  class=${this._actionClass("measure-custom")}
                  title="Özel yerleştir"
                  aria-label="Özel metre"
                  @click=${this._handleMeasureCustom}
                >
                  <svg class="svg container-svg">
                    <use xlink:href="#icon-measurements"></use>
                  </svg>
                </div>
              </div>`
            : ``}
        </div>`} `;
  }

  private _handleMeasureMenu() {
    const active = this._toggle("measure");
    this._emit("float-measure", { active });
  }

  private _handleMeasureAuto() {
    this._emit("float-measure-auto");
  }

  private _handleMeasureCustom() {
    this._emit("float-measure-custom");
  }

  private _setAxisMode(mode: "horizontal" | "vertical"): void {
    this._axisMode = mode;
    this.setAttribute("axis-mode", mode);
    this.requestUpdate();
    this._emit("float-axis-move", { mode });
  }

  private _previewAngle = (event: Event): void => {
    const degrees = Number.parseFloat(
      (event.currentTarget as HTMLInputElement).value,
    );
    this._emit("float-angle-input", { degrees });
  };

  private _commitAngle = (event: Event): void => {
    const degrees = Number.parseFloat(
      (event.currentTarget as HTMLInputElement).value,
    );
    this._emit("float-angle-change", { degrees });
  };
}

export function axisArrowIcon(
  mode: "horizontal" | "vertical",
): TemplateResult {
  if (mode === "horizontal") {
    return html`<svg viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
        d="M4 12h16M8 8 4 12l4 4M16 8l4 4-4 4"
      />
    </svg>`;
  }
  return html`<svg viewBox="0 0 24 24" aria-hidden="true">
    <path
      fill="none"
      stroke="currentColor"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
      d="M12 20V4M8 8l4-4 4 4"
    />
  </svg>`;
}

